import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const DropdownContainer = styled.div`
	position: relative;
	margin-left: 1rem;
`;

const DropdownButton = styled.button`
	display: flex;
	align-items: center;
	padding: 1rem;
	background-color: #2a2a2a;
	border: none;
	border-radius: 8px;
	color: var(--text-paragraph);
	font-size: 1rem;
	cursor: pointer;
	width: 140px;
`;

const DropdownList = styled.ul<{ top: number; left: number }>`
	position: absolute;
	top: ${(props) => props.top}px;
	left: ${(props) => props.left}px;
	width: 140px;
	background-color: #2a2a2a;
	border-radius: 0px 0px 8px 8px;
	list-style-type: none;
	padding: 0 0 8px 4px;
	margin: -3px 0 0;
	max-height: 250px;
	overflow-y: auto;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	z-index: 1000;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: #1e1e1e;
		border-radius: 8px;
	}

	&::-webkit-scrollbar-thumb {
		background-color: #4a4a4a;
		border-radius: 8px;
		border: 2px solid #1e1e1e;
	}

	scrollbar-width: thin;
	scrollbar-color: #4a4a4a #1e1e1e;
`;

const DropdownItem = styled.li`
	display: flex;
	align-items: center;
	padding: 8px 12px;
	cursor: pointer;
	color: var(--text-paragraph);

	&:hover {
		background-color: #3a3a3a;
	}
`;

const ChevronIcon = styled.svg`
	width: 16px;
	height: 16px;
	margin-left: auto;
`;

const ChevronDown = () => (
	<ChevronIcon
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M6 9L12 15L18 9"
			stroke="#aaaca7"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</ChevronIcon>
);

const ChevronUp = () => (
	<ChevronIcon
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M18 15L12 9L6 15"
			stroke="#aaaca7"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</ChevronIcon>
);

type DropdownProps<T> = {
	items: T[];
	selectedItem: T;
	onSelect: (item: T) => void;
	renderItem: (item: T) => React.ReactNode;
	renderSelectedItem: (item: T) => React.ReactNode;
	disabled?: boolean;
};

function Dropdown<T>({
	items,
	selectedItem,
	onSelect,
	renderItem,
	renderSelectedItem,
	disabled,
}: DropdownProps<T>) {
	const [isOpen, setIsOpen] = useState(false);
	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
	const buttonRef = useRef<HTMLButtonElement>(null);
	const dropdownRef = useRef<HTMLUListElement>(null);

	const handleToggleDropdown = () => {
		if (!isOpen && buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect();
			setDropdownPosition({
				top: rect.bottom + window.scrollY,
				left: rect.left + window.scrollX,
			});
		}
		setIsOpen(!isOpen);
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				buttonRef.current &&
				!buttonRef.current.contains(event.target as Node) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<DropdownContainer>
			<DropdownButton ref={buttonRef} onClick={handleToggleDropdown}>
				{renderSelectedItem(selectedItem)}
				{isOpen ? <ChevronUp /> : <ChevronDown />}
			</DropdownButton>
			{isOpen &&
				createPortal(
					<DropdownList
						ref={dropdownRef}
						top={dropdownPosition.top}
						left={dropdownPosition.left}
					>
						{items.map((item, index) => (
							<DropdownItem
								key={index}
								onClick={() => {
									onSelect(item);
									setIsOpen(false);
								}}
							>
								{renderItem(item)}
							</DropdownItem>
						))}
					</DropdownList>,
					document.body
				)}
		</DropdownContainer>
	);
}

export { Dropdown };
